<template>
  <content-modal
    class="message-modal resource-edit-modal"
    :visible="visible"
    :close-aria-label="closeAriaLabel"
    :size="size"
    :use-state-button="true"
    :auto-hide-off="true"
    @modal-shown="actionShown"
    @modal-hidden="actionHidden"
    @modal-hide-request="actionHideRequest"
  >
    <template v-if="use_title" #title>
      {{ t("resource_edit_modal_confirmation_title") }}
    </template>
    <template v-if="use_content" #content>
      <modal-content>
        <span class="info">{{ t("resource_edit_modal_confirmation_subtitle") }}</span>
        <div class="description">
          <h5 class="title">
            <svg-icon name="Info32" kind="indicative" />
            <span>{{ t("resource_edit_modal_note") }}</span>
          </h5>
        </div>

        <cv-list>
          <cv-list-item>
            <h6>{{ t("resource_edit_modal_access_title") }}</h6>
            <span>
              {{ t("resource_edit_modal_access_subtitle") }}
            </span>
          </cv-list-item>
          <cv-list-item v-if="isAlsLesson">
            <h6 class="title">{{ t("als_recommendations_paused_subtitle") }}</h6>
            <span class="text">{{ t("als_recommendations_paused_message") }}</span>
          </cv-list-item>
        </cv-list>

        <template #tools>
          <cv-button
            id="button_cancel"
            class="cancel"
            kind="secondary"
            size="field"
            @click="actionSecondary"
            >{{ t("button_edit_cancel") }}
          </cv-button>
          <fetch-data-button
            id="button_assignment_edit"
            kind="primary"
            size="field"
            :error="dataEditButton.error"
            :loading="dataEditButton.loading"
            :error-message="dataEditButton.errorMessage"
            @click="actionPrimary"
            >{{ t("button_assignment_edit_confirm") }}
          </fetch-data-button>
        </template>
      </modal-content>
    </template>
  </content-modal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import modalEventBus from "@plugins/modal/service/event-bus";
import { useVm } from "@utils/vm.ts";

interface Props {
  params?: {
    alsLesson?: boolean;
    actionPrimary?: () => void;
    actionSecondary?: () => void;
  };
}

const props = defineProps<Props>();

const { t } = useI18n();
const { vmNode } = useVm();

// State
const closeAriaLabel = ref("Close");
const use_title = ref(true);
const use_content = ref(true);
const size = ref("small");
const visible = ref(true);

const dataEditButton = ref({
  kind: "primary",
  error: false,
  loading: false,
  errorMessage: t("api_action_generic_error_message"),
});

// Computed
const isAlsLesson = computed(() => {
  return props.params?.alsLesson;
});

// Methods
const actionShown = (): void => {
  visible.value = true;
};

const actionHidden = (): void => {
  visible.value = false;
  modalEventBus.$emit("modal-hidden", vmNode.key);
};

const actionHideRequest = (event: { "cv:reason": string }): void => {
  // to prevent close modal upon clicking anywhere outside the modal container
  if (event["cv:reason"] !== "external-click") {
    actionHidden();
  }
};

const actionPrimary = (): void => {
  if (props.params?.actionPrimary) {
    props.params.actionPrimary();
  }
  visible.value = false;
};

const actionSecondary = (): void => {
  if (props.params?.actionSecondary && typeof props.params.actionSecondary === "function") {
    props.params.actionSecondary();
  }
  visible.value = false;
};

// Lifecycle
onMounted(() => {
  visible.value = true;
});
</script>
